<script setup lang="ts">
import { api } from '@/api';
import { type UserDto, UserStateOfUse } from '@/api/generator';
import { watchEffect, ref, useTemplateRef } from 'vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import SendActivationLinkModal from './components/SendActivationLinkModal.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import AvatarImage from '@/components/images/AvatarImage.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import { buildOdataQuery } from '@/utils/odata.ts';
import {
  globalContextMenuHeaderItemDefinition,
  getDefaultContextMenuActions,
} from '@/components/ContextMenu/utils';
import { routenames } from '@/router/routenames';
import { useRouterStore } from '@/stores/routerStore';
import { useTranslation } from 'i18next-vue';
import { useLoading } from '@/utils/useLoading';
import { useRouter } from 'vue-router';
import { useDeleteAccountWithConfirmation } from './utils';
import AccountOverviewFilter from './components/AccountOverviewFilter.vue';
import { AccountOverviewFilterObject } from './types';
import InfoMessage from '@/components/InfoMessage/InfoMessage.vue';
import type { I18nKey } from '@/utils/typings.ts';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { usePermissionsStore } from '@/stores/permissionsStore.ts';
import { AppPermissionsEnum } from '@/api/generator';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';
import { localizedEnumCompare } from '@/utils/compareAndSort';

const router = useRouter();
const routerStore = useRouterStore();
const { t } = useTranslation();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const headers = [
  {
    title: '',
    key: 'image',
    width: 72,
  },
  {
    title: t('Username'),
    key: 'lastName',
    sortable: true,
    width: '25%',
  },
  {
    title: t('AssignedRoles'),
    key: 'assignedRolesDisplayNames',
    width: 250,
    sortable: false,
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sortable: true,
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    title: t('LastSeen'),
    key: 'lastSeen',
    sortable: true,
  },
  globalContextMenuHeaderItemDefinition,
] as const;
const users = ref<UserDto[]>();

const filterObject = ref<AccountOverviewFilterObject>({});

const {
  action: loadUsers,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const { result } = await api.usersApi.getUserList(
    buildOdataQuery({
      search: filterObject.value?.search,
      filter: filterObject.value?.state?.length
        ? { state: { in: filterObject.value?.state } }
        : undefined,
    }),
  );

  if (result?.value) {
    users.value = result.value;
  }
}, true);

watchEffect(loadUsers);

const { action: deleteAction } = useDeleteAccountWithConfirmation();

const { hasPermissions } = usePermissionsStore();

const userCanAddNewUsers = hasPermissions([AppPermissionsEnum.GlobalAccountsAdd]);

function getContextMenuActions(item: UserDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: { name: routenames.accounts.details, params: { id: item.id } },
    },
    {
      ...defaultContextMenuActions.edit,
      to: { name: routenames.accounts.edit, params: { id: item.id } },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: async () => {
        const { result } = await deleteAction({
          param: item.id,
          confirmationOptions: getDefaultDeleteConfirmationOptions(
            {
              id: item.id,
              name: item.fullName,
              iuid: item.iuid,
              typeName: 'UserAccount_one',
            },
            t,
          ),
        });
        if (result?.success) {
          await loadUsers();
        }
      },
    },
  ];
}

function navigateTo(userId: number) {
  router.push({
    name: routenames.accounts.details,
    params: { id: userId },
  });
}

const showStateInfo = (item: UserDto) => item.state === UserStateOfUse.NoActivationSent;
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('UserAccount_other')" />
    </template>

    <template #actions>
      <PtwAddButton v-if="userCanAddNewUsers" :to="{ name: routenames.accounts.add }" />
    </template>
    <AccountOverviewFilter v-model="filterObject" />

    <OverviewTable
      ref="table"
      class="mt-2"
      :headers="headers"
      itemLabel="UserAccount"
      data-testid="DataTable_UserAccount"
      :items="users"
      :loading="loading"
      :sortBy="[{ key: 'lastName', order: 'asc' }]"
      :height="height"
      fixedHeader
      @click:row="
        (_event, data) => {
          navigateTo(data.item.id);
        }
      "
    >
      <template #header.lastName="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('Name') }}
          <span class="font-weight-medium">| {{ $t('ID') }}</span>
        </OverviewTableHeader>
      </template>

      <template #item.image="{ item }">
        <AvatarImage :pictureId="item.profilePictureId" />
      </template>
      <template #item.lastName="{ item }">
        <IdentifierLabel :identifier="item.iuid" :label="item.fullName" />
      </template>

      <template #[`item.assignedRolesDisplayNames`]="{ item }">
        {{ item.assignedRolesDisplayNames?.join(', ') ?? EMPTY_FIELD_SIGN }}
      </template>

      <template #item.lastSeen="{ item }">
        <span v-if="item.lastSeenNow">{{ $t('Now') }}</span>
        <DateTimeFormatter v-else-if="item.lastSeen" :value="item.lastSeen" />
        <span v-else>{{ EMPTY_FIELD_SIGN }}</span>
      </template>

      <template #item.state="{ item }">
        {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
        <InfoMessage v-if="showStateInfo(item)" :value="$t('NoActivationLinkSentMsg')" size="16" />
        <EntityChange
          :entityChange="getEntityChangeEvent(item, 'editing')"
          :showEmptyFieldSign="false"
        />
      </template>

      <template #item.contextMenu="{ item }">
        <ContextMenu
          :actions="getContextMenuActions(item)"
          :closeOnScrollElements="tableScrollElement"
        />
      </template>
    </OverviewTable>

    <SendActivationLinkModal v-model:user="routerStore.userAdded" @onSent="loadUsers" />
  </BasePage>
</template>
